/**
 * Las tarjetas de resumen que van arriba de una pantalla de revisión.
 *
 * Pedido del usuario (9-set): *«necesito indicadores en cada pantalla de revisión: asignación,
 * trabajadores, pdv, validación, registro de campo … mismos tipos de tarjetas pero que sea
 * relacionados a la data que se muestra obviamente … es para que el usuario tenga un visual
 * cuantitativo de resumen»*.
 *
 * Existía tres veces copiada y con DOS tipografías distintas —«Hoy» usaba `text-2xl font-semibold`
 * con el título en mayúsculas, y las bandejas de cierres y de visitas `text-xl font-bold` con el
 * título en minúsculas—, así que dos pantallas del mismo sistema mostraban el mismo tipo de dato
 * con distinto peso visual. Acá hay una sola implementación y todas se ven igual, que es
 * literalmente lo que pidió: *«mismos tipos de tarjetas»*.
 *
 * ## Dos decisiones que valen la pena explicar
 *
 * **`valor` es un número y no un texto.** Se probó con texto y las tarjetas empezaron a decir
 * cosas como «2 de 7 turnos» en el número grande, que ya no es un indicador: es una frase en
 * negrita. El número va solo y la aclaración va en `detalle`, que es lo que hace que la fila de
 * tarjetas se lea de un barrido.
 *
 * **En cero, el tono no pinta.** Un «Cierres que faltan: 0» en rojo enseña a ignorar el rojo, y
 * después el rojo que importa tampoco se ve. El tono colorea el número solo cuando hay algo que
 * mirar; en cero la tarjeta es gris como cualquier otra.
 */

/** Cómo se pinta el número cuando NO es cero. En cero siempre es neutro. */
export type TonoIndicador = 'neutro' | 'bueno' | 'atencion' | 'malo';

export interface Indicador {
  titulo: string;
  valor: number;
  /** La aclaración chica: «de 7 turnos», «3 vencidos», «sin cubrir 2 · sin marcar 1». */
  detalle?: string;
  tono?: TonoIndicador;
}

const TONO_CLS: Record<TonoIndicador, string> = {
  neutro: 'text-gray-900',
  bueno: 'text-green-700',
  atencion: 'text-amber-700',
  malo: 'text-red-700',
};

/**
 * Las columnas, como clases LITERALES y no armadas con plantillas.
 *
 * Tailwind descubre las clases leyendo el código fuente: un `grid-cols-${n}` construido en tiempo
 * de ejecución no existe en el CSS compilado y la fila de tarjetas sale apilada en producción
 * aunque en el servidor de desarrollo se vea bien. Por eso están escritas una por una.
 */
const COLUMNAS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
};

/** Una tarjeta suelta. Se exporta porque hay pantallas que ponen una sola, fuera de la fila. */
export function Tarjeta({ titulo, valor, detalle, tono = 'neutro' }: Indicador) {
  return (
    <div className="min-w-0 rounded border border-gray-200 bg-white p-3">
      {/* En celular la tarjeta mide ~140px: el título envuelve en varias líneas en vez de cortarse. */}
      <p className="break-words text-xs uppercase tracking-wide text-gray-500">{titulo}</p>
      <p className={`text-2xl font-semibold ${valor === 0 ? TONO_CLS.neutro : TONO_CLS[tono]}`}>
        {valor}
      </p>
      {detalle !== undefined && detalle !== ''
        && <p className="break-words text-xs text-gray-500">{detalle}</p>}
    </div>
  );
}

/**
 * La fila de indicadores de una pantalla.
 *
 * Los `null` de la lista se descartan, así que cada pantalla puede escribir tarjetas condicionales
 * en línea —`puedeClaves ? {...} : null`— sin armar el arreglo aparte.
 */
export default function TarjetasResumen(
  { indicadores, className }: { indicadores: (Indicador | null)[]; className?: string },
) {
  const items = indicadores.filter((x): x is Indicador => x !== null);
  if (items.length === 0) return null;
  const cols = COLUMNAS[items.length] ?? COLUMNAS[6];
  return (
    <div className={`grid gap-2 ${cols} ${className ?? 'mb-4'}`}>
      {items.map((i) => <Tarjeta key={i.titulo} {...i} />)}
    </div>
  );
}
